import type {
    BehaviourDayDto,
    BiomassDayDto,
    BiomassEstimateDto,
    BiomassOverviewDaySceneLabelDto,
    BiomassOverviewFilterDto,
    BiomassOverviewResponseDto,
    BiomassOverviewSceneLabelDto,
    BiomassPeriodDto,
    BioscopeDayDto,
    EnvironmentalBlockDto,
} from '@/services/types';
import { toWallClock } from '@/utils/date';

import { toOptionalFiniteNumber } from '../shared/numbers';

import type {
    DomainBehaviourDay,
    DomainBiomassDay,
    DomainBioscopeDay,
    DomainCageOverview,
    DomainCageOverviewEnvironmental,
    DomainCageOverviewEstimate,
    DomainCageOverviewFilter,
    DomainCageOverviewPeriod,
    DomainCageOverviewSceneLabel,
    DomainDaySceneLabel,
} from './overview.types';

/**
 * The generated DTOs mark absent numbers as optional rather than nullable, so
 * every metric goes through this: `undefined` and `null` both become `null`, and
 * consumers never have to distinguish the two.
 */
function toMetric(value: number | null | undefined): number | null {
    return value ?? null;
}

export function mapCageOverviewFilterToDomain(
    dto: BiomassOverviewFilterDto | undefined
): DomainCageOverviewFilter {
    return {
        dayFrom: dto?.dayFrom ?? '',
        dayTo: dto?.dayTo ?? '',
        daypartTag: dto?.daypartTag ?? '',
        weightUnitTag: dto?.weightUnitTag ?? '',
    };
}

export function mapCageOverviewEstimateToDomain(
    dto: BiomassEstimateDto | undefined
): DomainCageOverviewEstimate {
    return {
        lastDay: dto?.lastDay ?? '',
        estimatedWeight: toMetric(dto?.estimatedWeight),
        measStatusLevel: toMetric(dto?.measStatusLevel),
        measStatusList: dto?.measStatusList ?? [],
    };
}

export function mapCageOverviewPeriodToDomain(
    dto: BiomassPeriodDto | undefined
): DomainCageOverviewPeriod {
    return {
        measCnt: toMetric(dto?.measCnt),
        weight: toMetric(dto?.weight),
        weightGrowth: toMetric(dto?.weightGrowth),
        cv: toMetric(dto?.cv),
        sgr: toMetric(dto?.sgr),
        rgi: toMetric(dto?.rgi),
        cf: toMetric(dto?.cf),
        cfGrowth: toMetric(dto?.cfGrowth),
        expectedCf: toMetric(dto?.expectedCf),
    };
}

export function mapBiomassDayToDomain(dto: BiomassDayDto): DomainBiomassDay {
    return {
        day: dto.day,
        daypartTag: dto.daypartTag,
        data: {
            measCnt: toMetric(dto.data?.measCnt),
            weightAvg: toMetric(dto.data?.weightAvg),
            weightTrend: toMetric(dto.data?.weightTrend),
            weightConf: toMetric(dto.data?.weightConf),
            weightConfHi: toMetric(dto.data?.weightConfHi),
            weightConfLo: toMetric(dto.data?.weightConfLo),
            weightGrowthAvg: toMetric(dto.data?.weightGrowthAvg),
            weightGrowthTrend: toMetric(dto.data?.weightGrowthTrend),
            cv: toMetric(dto.data?.cv),
            cfAvg: toMetric(dto.data?.cfAvg),
            cfTrend: toMetric(dto.data?.cfTrend),
            fishLengthAvg: toMetric(dto.data?.fishLengthAvg),
            fishHeightAvg: toMetric(dto.data?.fishHeightAvg),
        },
    };
}

function mapDaySceneLabelToDomain(dto: BiomassOverviewDaySceneLabelDto): DomainDaySceneLabel {
    return {
        tag: dto.tag,
        count: dto.count ?? 0,
        relativeCount: dto.relativeCount ?? 0,
    };
}

export function mapBioscopeDayToDomain(dto: BioscopeDayDto): DomainBioscopeDay {
    return {
        day: dto.day,
        daypartTag: dto.daypartTag,
        data: {
            depthMin: toMetric(dto.data?.depthMin),
            depthMax: toMetric(dto.data?.depthMax),
            depthAvg: toMetric(dto.data?.depthAvg),
            temperature: toMetric(dto.data?.temperature),
            visibilityMin: toMetric(dto.data?.visibilityMin),
            visibilityMax: toMetric(dto.data?.visibilityMax),
            visibilityAvg: toMetric(dto.data?.visibilityAvg),
            verticalDisparity: toMetric(dto.data?.verticalDisparity),
            bioscopeRollAvg: toMetric(dto.data?.bioscopeRollAvg),
            bioscopePitchAvg: toMetric(dto.data?.bioscopePitchAvg),
        },
        sceneLabels: (dto.sceneLabels ?? []).map(mapDaySceneLabelToDomain),
    };
}

export function mapBehaviourDayToDomain(dto: BehaviourDayDto): DomainBehaviourDay {
    return {
        day: dto.day,
        daypartTag: dto.daypartTag,
        data: {
            fishSpeedAvg: toMetric(dto.data?.fishSpeedAvg),
            fishSpeedYAvg: toMetric(dto.data?.fishSpeedYAvg),
            fishSpeed3dAvg: toMetric(dto.data?.fishSpeed3dAvg),
        },
    };
}

export function mapCageOverviewSceneLabelToDomain(
    dto: BiomassOverviewSceneLabelDto
): DomainCageOverviewSceneLabel {
    return {
        sceneLabelTag: dto.sceneLabelTag,
        count: dto.count ?? 0,
        relativeCount: dto.relativeCount ?? 0,
        daypartTag: dto.daypartTag,
        dataRangeTag: dto.dataRangeTag,
    };
}

export function mapEnvironmentalToDomain(
    dto: EnvironmentalBlockDto | null | undefined
): DomainCageOverviewEnvironmental | null {
    if (dto == null) {
        return null;
    }

    const mapped: DomainCageOverviewEnvironmental = {
        lastMeasurement: toWallClock(dto.lastMeasurement),
        oxygenSaturation: toOptionalFiniteNumber(dto.oxygenSaturation),
        oxygenConcentration: toOptionalFiniteNumber(dto.oxygenConcentration),
        salinity: toOptionalFiniteNumber(dto.salinity),
        temperature: toOptionalFiniteNumber(dto.temperature),
        depth: toOptionalFiniteNumber(dto.depth),
    };

    const hasMetric =
        mapped.oxygenSaturation != null ||
        mapped.oxygenConcentration != null ||
        mapped.salinity != null ||
        mapped.temperature != null ||
        mapped.depth != null;

    return hasMetric ? mapped : null;
}

/**
 * Maps the whole response, tolerating an absent one.
 *
 * An overview that never arrived maps to the same empty shape as one that came
 * back with nothing, so consumers read `data.biomass.days` without guarding and
 * signal "no data yet" from `isLoading` or from the absence of a `lastDay`.
 */
export function mapCageOverviewToDomain(
    dto: BiomassOverviewResponseDto | undefined
): DomainCageOverview {
    return {
        filter: mapCageOverviewFilterToDomain(dto?.filter),
        biomass: {
            estimate: mapCageOverviewEstimateToDomain(dto?.biomass?.estimate),
            period: mapCageOverviewPeriodToDomain(dto?.biomass?.period),
            days: (dto?.biomass?.days ?? []).map(mapBiomassDayToDomain),
        },
        bioscope: {
            days: (dto?.bioscope?.days ?? []).map(mapBioscopeDayToDomain),
            sceneLabels: (dto?.bioscope?.sceneLabels ?? []).map(mapCageOverviewSceneLabelToDomain),
        },
        behaviour: {
            days: (dto?.behaviour?.days ?? []).map(mapBehaviourDayToDomain),
        },
        environmental: mapEnvironmentalToDomain(dto?.environmental),
    };
}
